// Persistent DCP-pipeline progress strip. Rendered at the top of
// every DCP workflow page (Venues, Sources, Library, Create,
// Distribution) so the operator has a constant mental model of the
// four-step pipeline and where the blockers are. Added in v11.0.0 per
// the workflow audit: "no persistent mental model of the pipeline."
//
// Each segment is clickable, routes to the relevant page, and
// color-codes by blocker state:
//   - default border + neutral fill: no blockers
//   - yellow border: action recommended (e.g. upcoming bookings)
//   - orange border + warning fill: action required (e.g. missing certs)
//
// Data feed: /api/dcp-pipeline/status (single endpoint, see
// backend/api/dcp_pipeline.py). Polls every 30 s; idle when the tab
// is hidden so a backgrounded dashboard doesn't burn cycles.
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, Tooltip, useTheme, alpha,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as ReadyIcon,
  EventNote as CalendarIcon,
} from '@mui/icons-material';
import api from '../services/api';

// Public so tests can target the segment ids directly without coupling
// to the visible label (the label is allowed to change; the contract
// with /api/dcp-pipeline/status is what's pinned).
// `to` routes: `/dcp` is the Queue (active jobs only); the Library
// (terminal / completed / archived jobs — including the "ready to
// distribute" ones the strip surfaces) lives at `/library`. Pre-fix
// the Library segment opened the Queue, which has the opposite
// filter; operator caught this on the first day of v11.0.0.
export const PIPELINE_STEPS = [
  { key: 'venues',       label: 'Venues',       to: '/venues' },
  { key: 'sources',      label: 'Sources',      to: '/sources' },
  { key: 'library',      label: 'DCP Library',  to: '/library' },
  { key: 'distribution', label: 'Distribution', to: '/distribution' },
];

// Pure: pick the chip color + helper text from a per-step counters
// object. Exported so the test can pin the color/text policy without
// rendering the component.
//   - "venues":       blocker = missing_certs > 0
//   - "sources":      neutral; counts are informational
//   - "library":      info pulse when ready_to_distribute > 0
//   - "distribution": warn when undelivered_upcoming > 0, info pulse on upcoming_7d
export function stepStateFor(stepKey, counts) {
  if (!counts) return { tone: 'idle', sub: '' };
  if (stepKey === 'venues') {
    const missing = counts.missing_certs || 0;
    if (missing > 0) {
      return { tone: 'blocker', sub: `${missing} missing cert${missing === 1 ? '' : 's'}` };
    }
    return { tone: 'idle', sub: `${counts.total || 0} on file` };
  }
  if (stepKey === 'sources') {
    // v11.0.2 — count tracks the operator's "Available for DCP"
    // toggle, not compliance state. Falls back to the old key during
    // a brief in-flight deploy window where the strip might receive
    // pre-rename payloads.
    const avail = counts.available_for_dcp != null
      ? counts.available_for_dcp
      : (counts.ready_for_dcp || 0);
    return { tone: 'idle', sub: `${avail} available for DCP` };
  }
  if (stepKey === 'library') {
    const ready = counts.ready_to_distribute || 0;
    if (ready > 0) {
      return { tone: 'ready', sub: `${ready} ready to distribute` };
    }
    return { tone: 'idle', sub: `${counts.total || 0} total` };
  }
  if (stepKey === 'distribution') {
    const sent = counts.sent || 0;
    const upcoming = counts.upcoming_7d || 0;
    const undelivered = counts.undelivered_upcoming || 0;
    if (undelivered > 0) {
      return {
        tone: 'blocker',
        sub: `${undelivered} not yet delivered (next 7d)`,
      };
    }
    if (upcoming > 0) {
      // v11.0.2 — upcoming bookings that are ALL delivered should
      // read as a positive signal (green ready tone), not the yellow
      // "warning" tone. Pre-fix the strip showed "1 screening in 7d"
      // in yellow even when the DCP + KDM were already sent — looked
      // like an open todo item when nothing needed doing. The "all
      // delivered" word in the sub keeps the operator from second-
      // guessing.
      return {
        tone: 'ready',
        sub: `${upcoming} screening${upcoming === 1 ? '' : 's'} in 7d · all delivered`,
      };
    }
    return { tone: 'idle', sub: `${sent} sent` };
  }
  return { tone: 'idle', sub: '' };
}

export default function PipelineProgress() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get('/api/dcp-pipeline/status');
        if (!cancelled) setData(r.data);
      } catch (_e) { /* best-effort — strip degrades to a static label set */ }
    };
    tick();
    const iv = setInterval(() => {
      if (!document.hidden) tick();
    }, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const tones = {
    idle: {
      border: alpha(theme.palette.divider, 0.4),
      bg: 'background.paper',
      fg: 'text.secondary',
    },
    ready: {
      border: theme.palette.info.main,
      bg: alpha(theme.palette.info.main, 0.08),
      fg: 'info.main',
    },
    upcoming: {
      border: theme.palette.warning.main,
      bg: alpha(theme.palette.warning.main, 0.08),
      fg: 'warning.dark',
    },
    blocker: {
      border: theme.palette.error.main,
      bg: alpha(theme.palette.error.main, 0.12),
      fg: 'error.main',
    },
  };

  return (
    <Box data-testid="pipeline-progress" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: 'wrap' }}>
        {PIPELINE_STEPS.map((step, idx) => {
          const counts = data ? data[step.key] : null;
          const state = stepStateFor(step.key, counts);
          const tone = tones[state.tone] || tones.idle;
          const active = location.pathname === step.to
            || (step.to !== '/' && location.pathname.startsWith(step.to + '/'));
          return (
            <Tooltip
              key={step.key}
              title={`Open ${step.label}`}
              arrow
              placement="bottom"
            >
              <Paper
                onClick={() => navigate(step.to)}
                role="button"
                tabIndex={0}
                data-testid={`pipeline-step-${step.key}`}
                data-tone={state.tone}
                sx={{
                  cursor: 'pointer',
                  flex: '1 1 200px',
                  px: 2, py: 1,
                  border: 2,
                  borderColor: tone.border,
                  bgcolor: tone.bg,
                  outline: active ? `2px solid ${theme.palette.primary.main}` : 'none',
                  outlineOffset: -3,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  },
                  transition: 'background-color 0.15s, border-color 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 18 }}
                  >
                    {idx + 1}.
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {step.label}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {state.tone === 'blocker' && <WarningIcon fontSize="small" sx={{ color: 'error.main' }} />}
                  {state.tone === 'ready' && <ReadyIcon fontSize="small" sx={{ color: 'info.main' }} />}
                  {state.tone === 'upcoming' && <CalendarIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.25, color: tone.fg, fontWeight: state.tone === 'blocker' ? 600 : 400 }}
                >
                  {state.sub || ' '}
                </Typography>
              </Paper>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
